import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { GraduationCap, Calculator, Target, PiggyBank, TrendingUp, Home, Calendar, Baby, Unlock } from 'lucide-react';
import { mockCalculateEducationPlan, educationCosts } from '../mock/educationMock';
import LeadCaptureModal from './LeadCaptureModal';

const EducationPlanner = () => {
  const navigate = useNavigate();

  // Set page title
  React.useEffect(() => {
    document.title = 'Education Fund Planner - AUMONLINE';
  }, []);

  const [formData, setFormData] = useState({
    childName: '',
    ageYears: '',
    ageMonths: '0',
    profession: '',
    customProfession: '',
    selectedFees: '',
    customFees: '',
    currentSavings: '',
    educationInflation: '10'
  });

  const [results, setResults] = useState(null);
  const [showCostSuggestions, setShowCostSuggestions] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showFullResults, setShowFullResults] = useState(false);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfessionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      profession: value,
      customProfession: value === 'Others' ? prev.customProfession : '',
      selectedFees: '',
      customFees: ''
    }));
    
    if (value !== 'Others' && value !== '') {
      setShowCostSuggestions(true);
    } else {
      setShowCostSuggestions(false);
    }
  };

  const handleFeesSelection = (type) => {
    const profession = formData.profession === 'Others' ? formData.customProfession : formData.profession;
    const cost = educationCosts[profession]?.[type] || 0;
    setFormData(prev => ({
      ...prev,
      selectedFees: type,
      customFees: cost.toString()
    }));
  };

  const handleCostSuggestionClick = (cost) => {
    setFormData(prev => ({
      ...prev,
      customFees: cost.toString()
    }));
  };

  // Calculate child's age and years to 18
  const ageCalculations = useMemo(() => {
    if (formData.ageYears === '' || formData.ageYears === null || formData.ageYears === undefined) return null;

    const years = parseInt(formData.ageYears, 10);
    const months = parseInt(formData.ageMonths || '0', 10);

    if (isNaN(years) || years < 0 || years > 17) return null;
    if (isNaN(months) || months < 0 || months > 11) return null;

    const ageDecimal = years + months / 12;
    const yearsTo18 = Math.max(0, 18 - ageDecimal);

    return {
      currentAge: years,
      ageMonths: months,
      yearsTo18: Math.round(yearsTo18 * 100) / 100,
      growthRate: yearsTo18 < 5 ? 12 : 15
    };
  }, [formData.ageYears, formData.ageMonths]);

  const handleCalculate = () => {
    // Validate inputs
    const requiredFields = ['childName', 'ageYears', 'profession', 'customFees', 'educationInflation'];
    const hasEmptyFields = requiredFields.some(field => {
      if (field === 'profession') {
        return !formData.profession || (formData.profession === 'Others' && !formData.customProfession);
      }
      return formData[field] === '' || formData[field] === null || formData[field] === undefined;
    });

    if (hasEmptyFields || !ageCalculations) {
      alert('Please fill in all required fields');
      return;
    }

    if (ageCalculations.currentAge >= 18) {
      alert('Child is already 18 or older. This planner is for future education funding.');
      return;
    }

    const calculatedResults = mockCalculateEducationPlan({
      ...formData,
      ...ageCalculations
    });
    setResults(calculatedResults);
    setShowPreview(true);
    setShowFullResults(false);
  };

  const handleUnlockFullRoadmap = () => {
    setIsLeadModalOpen(true);
  };

  const handleLeadSubmit = async (leadData) => {
    setIsSubmittingLead(true);
    
    try {
      const webhookPayload = {
        lead_source: 'AUMOnline Planner',
        tool_used: "Children's Future Education Fund Planner",
        user_name: leadData.name,
        user_phone: leadData.phone,
        key_metrics: {
          target_corpus: results?.futureEducationCost,
          monthly_investment_needed: results?.sipRequired
        }
      };

      const response = await fetch('https://n8n.srv1138087.hstgr.cloud/webhook/planner-lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookPayload)
      });

      if (response.ok) {
        setIsLeadModalOpen(false);
        setShowFullResults(true);
      } else {
        let errorText = 'Unknown error';
        try {
          errorText = await response.text();
        } catch (e) {
          console.error('Could not read error response:', e);
        }
        throw new Error(`Failed to submit: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`There was an error submitting your details: ${errorMessage}. Please try again.`);
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const handleReset = () => {
    setFormData({
      childName: '',
      ageYears: '',
      ageMonths: '0',
      profession: '',
      customProfession: '',
      selectedFees: '',
      customFees: '',
      currentSavings: '',
      educationInflation: '10'
    });
    setResults(null);
    setShowCostSuggestions(false);
    setShowPreview(false);
    setShowFullResults(false);
  };

  const getCurrentProfession = () => {
    return formData.profession === 'Others' ? formData.customProfession : formData.profession;
  };

  const professionOptions = [
    'Engineering', 'Architecture', 'MBBS', 'B.A LLB', 'Pharma', 
    'B.Com', 'BBA', 'B.Des', 'BHM', 'Others'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-between items-center mb-8">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex items-center gap-2 border-2 border-green-700 text-green-700 hover:bg-green-700 hover:text-white transition-colors"
            >
              <Home className="w-5 h-5" />
              Back to Home
            </Button>
            
            <div className="flex-1 flex flex-col items-center gap-4">
              <a href="https://www.aumonline.in" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/5j61poys_Logo.jpg" 
                  alt="AUMONLINE Logo" 
                  className="h-20 md:h-24 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                />
              </a>
              <a href="https://www.njwealth.in" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/0iyf1b7c_image.png" 
                  alt="NJ WEALTH Logo" 
                  className="h-14 md:h-16 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                />
              </a>
            </div>
            
            <div className="w-32"></div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            CHILDREN'S FUTURE EDUCATION FUND PLANNER
          </h1>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-8xl mx-auto">
          {/* Input Section */}
          <Card className="shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-green-700 to-orange-600 text-white">
              <CardTitle className="text-2xl md:text-3xl text-center flex items-center justify-center gap-3">
                <GraduationCap className="w-8 h-8" />
                Plan Your Child's Education
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <div className="space-y-6">
                {/* Child's Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="childName" className="text-lg font-semibold text-gray-700">
                      Child's Name
                    </Label>
                    <Input
                      id="childName"
                      name="childName"
                      type="text"
                      value={formData.childName}
                      onChange={handleInputChange}
                      placeholder="Enter child's name"
                      className="text-lg p-4 border-2 border-gray-300 focus:border-green-700"
                      data-testid="child-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-lg font-semibold text-gray-700">
                      Child's Current Age
                    </Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Input
                          id="ageYears"
                          name="ageYears"
                          type="number"
                          inputMode="numeric"
                          min="0"
                          max="17"
                          value={formData.ageYears}
                          onChange={handleInputChange}
                          placeholder="Years"
                          className="text-lg p-4 border-2 border-gray-300 focus:border-green-700"
                          data-testid="child-age-years-input"
                        />
                        <p className="text-xs text-gray-500 mt-1 text-center">Years (0-17)</p>
                      </div>
                      <div>
                        <Input
                          id="ageMonths"
                          name="ageMonths"
                          type="number"
                          inputMode="numeric"
                          min="0"
                          max="11"
                          value={formData.ageMonths}
                          onChange={handleInputChange}
                          placeholder="Months"
                          className="text-lg p-4 border-2 border-gray-300 focus:border-green-700"
                          data-testid="child-age-months-input"
                        />
                        <p className="text-xs text-gray-500 mt-1 text-center">Months (0-11)</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Age Display */}
                {ageCalculations && (
                  <div className="bg-gradient-to-r from-blue-100 to-green-100 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Baby className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold">
                          Age: {ageCalculations.currentAge}y{ageCalculations.ageMonths > 0 ? ` ${ageCalculations.ageMonths}m` : ''}
                        </span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <Calendar className="w-5 h-5 text-green-600" />
                        <span className="font-semibold">Years to 18: {ageCalculations.yearsTo18}</span>
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <TrendingUp className="w-5 h-5 text-orange-600" />
                        <span className="font-semibold">Growth Rate: {ageCalculations.growthRate}%</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Profession Selection */}
                <div className="space-y-2">
                  <Label className="text-lg font-semibold text-gray-700">
                    What does your child want to study?
                  </Label>
                  <Select value={formData.profession} onValueChange={handleProfessionChange}>
                    <SelectTrigger className="text-lg p-4 border-2 border-gray-300 focus:border-green-700">
                      <SelectValue placeholder="Select profession" />
                    </SelectTrigger>
                    <SelectContent>
                      {professionOptions.map((profession) => (
                        <SelectItem key={profession} value={profession}>
                          {profession}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Profession Input */}
                {formData.profession === 'Others' && (
                  <div className="space-y-2">
                    <Label htmlFor="customProfession" className="text-lg font-semibold text-gray-700">
                      Specify the profession
                    </Label>
                    <Input
                      id="customProfession"
                      name="customProfession"
                      type="text"
                      value={formData.customProfession}
                      onChange={handleInputChange}
                      placeholder="Enter the desired profession"
                      className="text-lg p-4 border-2 border-gray-300 focus:border-green-700"
                    />
                  </div>
                )}

                {/* Cost Suggestions */}
                {showCostSuggestions && getCurrentProfession() && educationCosts[getCurrentProfession()] && (
                  <div className="space-y-4">
                    <Label className="text-lg font-semibold text-gray-700">
                      Current Education Cost Suggestions for {getCurrentProfession()}:
                    </Label>
                    <div className="grid grid-cols-1 gap-3">
                      {Object.entries(educationCosts[getCurrentProfession()]).map(([type, cost]) => (
                        <Button
                          key={type}
                          variant="outline"
                          onClick={() => handleCostSuggestionClick(cost)}
                          className={`p-4 text-left border-2 transition-colors ${
                            formData.customFees === cost.toString()
                              ? 'border-green-700 bg-green-50 text-green-700' 
                              : 'border-gray-300 hover:border-green-700'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-semibold">{type}</span>
                            <span className="text-lg font-bold">₹{cost.toLocaleString('en-IN')}</span>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Custom Fees Input */}
                <div className="space-y-2">
                  <Label htmlFor="customFees" className="text-lg font-semibold text-gray-700">
                    Education Cost (Complete Course)
                  </Label>
                  <Input
                    id="customFees"
                    name="customFees"
                    type="number"
                    value={formData.customFees}
                    onChange={handleInputChange}
                    placeholder="₹ Enter total course fees"
                    className="text-lg p-4 border-2 border-gray-300 focus:border-green-700"
                  />
                </div>

                {/* Current Savings Input */}
                <div className="space-y-2">
                  <Label htmlFor="currentSavings" className="text-lg font-semibold text-gray-700">
                    Current Savings for this Goal (Optional)
                  </Label>
                  <Input
                    id="currentSavings"
                    name="currentSavings"
                    type="number"
                    value={formData.currentSavings}
                    onChange={handleInputChange}
                    placeholder="₹ Enter existing savings"
                    className="text-lg p-4 border-2 border-gray-300 focus:border-green-700"
                  />
                  <p className="text-sm text-gray-500">
                    If you have already saved some amount for this goal, enter it here
                  </p>
                </div>

                {/* Education Inflation */}
                <div className="space-y-2">
                  <Label htmlFor="educationInflation" className="text-lg font-semibold text-gray-700">
                    Education Inflation Rate (% per annum)
                  </Label>
                  <Input
                    id="educationInflation"
                    name="educationInflation"
                    type="number"
                    step="0.1"
                    value={formData.educationInflation}
                    onChange={handleInputChange}
                    placeholder="10"
                    className="text-lg p-4 border-2 border-gray-300 focus:border-green-700"
                  />
                  <p className="text-sm text-gray-500">
                    Current education inflation in India: 8-12% per annum
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="text-center space-y-4">
                  <Button
                    onClick={handleCalculate}
                    disabled={!ageCalculations}
                    className="w-full bg-gradient-to-r from-green-700 to-orange-600 hover:from-green-800 hover:to-orange-700 text-white px-8 py-4 text-xl font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                  >
                    <Calculator className="w-6 h-6 mr-3" />
                    CALCULATE EDUCATION FUND
                  </Button>

                  {results && (
                    <Button
                      onClick={handleReset}
                      className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-3 text-lg font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                    >
                      RESET
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Section */}
          {results && showPreview && (
            <div className="space-y-6">
              <Card className="shadow-xl border-0">
                <CardHeader className="bg-gradient-to-r from-orange-600 to-yellow-600 text-white">
                  <CardTitle className="text-2xl md:text-3xl text-center">
                    Education Fund Plan for {formData.childName}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-8">
                  <div className="space-y-6">
                    <div className="bg-gradient-to-br from-green-100 to-green-50 p-6 rounded-xl border-l-4 border-green-700">
                      <div className="flex items-center gap-3 mb-3">
                        <GraduationCap className="w-8 h-8 text-green-700" />
                        <h3 className="text-xl font-bold text-gray-800">Future Education Cost</h3>
                      </div>
                      <p className="text-4xl font-bold text-green-700">
                        ₹{results?.futureEducationCost?.toLocaleString('en-IN', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        When {formData.childName} turns 18 (in {results?.yearsTo18} years)
                      </p>
                    </div>

                    {!showFullResults && (
                      <div className="text-center space-y-4">
                        <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
                          <p className="text-lg text-gray-700 mb-4">
                            🔒 Want to see the complete breakdown including:
                          </p>
                          <ul className="text-left text-gray-600 space-y-2 mb-4 max-w-md mx-auto">
                            <li>• Current education cost</li>
                            <li>• Exact monthly SIP required</li>
                            <li>• Impact of existing savings</li>
                            <li>• Comprehensive investment summary</li>
                          </ul>
                        </div>
                        <Button
                          onClick={handleUnlockFullRoadmap}
                          className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-4 text-xl font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                        >
                          <Unlock className="w-6 h-6 mr-3" />
                          Unlock Full Roadmap
                        </Button>
                      </div>
                    )}

                    {showFullResults && (
                      <div className="bg-gradient-to-br from-blue-100 to-blue-50 p-6 rounded-xl border-l-4 border-blue-800">
                        <div className="flex items-center gap-3 mb-3">
                          <Target className="w-8 h-8 text-blue-800" />
                          <h3 className="text-xl font-bold text-gray-800">Current Education Cost</h3>
                        </div>
                        <p className="text-3xl font-bold text-blue-800">
                          ₹{results?.currentCost?.toLocaleString('en-IN', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {showFullResults && (
                <Card className="shadow-xl border-0">
                  <CardHeader className="bg-gradient-to-r from-green-700 to-blue-800 text-white">
                    <CardTitle className="text-2xl md:text-3xl text-center">
                      SIP Investment Plan
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-8">
                    <div className="space-y-6">
                      <div className="bg-gradient-to-br from-orange-100 to-orange-50 p-6 rounded-xl border-l-4 border-orange-600">
                        <div className="flex items-center gap-3 mb-3">
                          <Calculator className="w-8 h-8 text-orange-600" />
                          <h3 className="text-xl font-bold text-gray-800">SIP Required Per Month</h3>
                        </div>
                        <p className="text-3xl font-bold text-orange-600">
                          ₹{results?.sipRequired?.toLocaleString('en-IN', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          })}
                        </p>
                        {results?.shortfallAfterSavings !== undefined && results?.shortfallAfterSavings < results?.futureEducationCost && (
                          <p className="text-sm text-gray-600 mt-2">
                            (Adjusted for existing savings of ₹{formData.currentSavings ? parseFloat(formData.currentSavings).toLocaleString('en-IN') : '0'})
                          </p>
                        )}
                      </div>

                      <div className="bg-gradient-to-br from-yellow-100 to-yellow-50 p-6 rounded-xl border-l-4 border-yellow-600">
                        <div className="flex items-center gap-3 mb-3">
                          <PiggyBank className="w-8 h-8 text-yellow-600" />
                          <h3 className="text-xl font-bold text-gray-800">Total Corpus Generated</h3>
                        </div>
                        <p className="text-3xl font-bold text-yellow-600">
                          ₹{results?.totalCorpusGenerated?.toLocaleString('en-IN', {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          })}
                        </p>
                      </div>

                      {results?.futureValueOfSavings > 0 && (
                        <div className="bg-gradient-to-br from-purple-100 to-purple-50 p-6 rounded-xl border-l-4 border-purple-600">
                          <div className="flex items-center gap-3 mb-3">
                            <TrendingUp className="w-8 h-8 text-purple-600" />
                            <h3 className="text-xl font-bold text-gray-800">Growth of Current Savings</h3>
                          </div>
                          <p className="text-3xl font-bold text-purple-600">
                            ₹{results?.futureValueOfSavings?.toLocaleString('en-IN', {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0
                            })}
                          </p>
                          <p className="text-sm text-gray-600 mt-2">
                            Future value of your existing ₹{formData.currentSavings ? parseFloat(formData.currentSavings).toLocaleString('en-IN') : '0'}
                          </p>
                        </div>
                      )}

                      <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-lg p-4">
                        <h4 className="font-bold text-gray-800 mb-2">Investment Summary:</h4>
                        <div className="text-sm space-y-1 text-gray-700">
                          <p>• Investment Duration: {results?.yearsTo18} years</p>
                          <p>• Expected Returns: {results?.growthRate}% per annum</p>
                          <p>• Total SIP Investment: ₹{Math.round(results?.sipRequired * results?.yearsTo18 * 12)?.toLocaleString('en-IN')}</p>
                          {results?.futureValueOfSavings > 0 && (
                            <p>• Current Savings Contribution: ₹{results?.futureValueOfSavings?.toLocaleString('en-IN')}</p>
                          )}
                          <p>• Total Corpus at Maturity: ₹{(results?.totalCorpusGenerated + (results?.futureValueOfSavings || 0))?.toLocaleString('en-IN')}</p>
                          <p>• Expected Surplus: ₹{((results?.totalCorpusGenerated + (results?.futureValueOfSavings || 0)) - results?.futureEducationCost)?.toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Lead Capture Modal */}
        <LeadCaptureModal
          isOpen={isLeadModalOpen}
          onClose={() => setIsLeadModalOpen(false)}
          onSubmit={handleLeadSubmit}
          toolName="Children's Future Education Fund Planner"
          isSubmitting={isSubmittingLead}
        />

        {/* SEBI Compliance Disclaimer */}
        <div className="mt-12 pt-8 border-t border-gray-300 max-w-8xl mx-auto">
          <div className="text-center">
            <p className="text-lg font-bold text-red-600">
              Mutual Fund investments are subject to market risks. Read all scheme-related documents carefully before investing
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationPlanner;